import { Component, OnInit, OnDestroy } from '@angular/core'
import { NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { filter } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { CognitoService } from './common/api/services/cognito.service'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [RouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'WIR Angular'

  private static readonly CONSENT_KEY = 'wir_cookie_consent_given'
  private bannerObserver: MutationObserver | null = null

  constructor (
    private cognitoService: CognitoService,
    private router: Router
  ) {}

  ngOnInit (): void {
    // Initialize MSAL and handle any redirect response from Azure AD
    this.cognitoService.initialize()
    this.injectGtm()

    // Only load the OneTrust cookie banner AFTER MSAL redirect handling is
    // complete and the app has settled on a route. This prevents the banner
    // from appearing during the transient redirect page loads that happen
    // as part of the Azure AD SSO flow.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Do not show the cookie banner on the auth redirect route —
        // the user is about to be navigated away immediately.
        if (event.urlAfterRedirects !== '/redirect') {
          this.loadOneTrustScript()
        }
      })
  }

  ngOnDestroy (): void {
    if (this.bannerObserver) {
      this.bannerObserver.disconnect()
      this.bannerObserver = null
    }
  }

  private loadOneTrustScript (): void {
    if (!environment.oneTrustDomainScript) {
      return
    }

    // If the user has already interacted with the cookie banner,
    // do not load the OneTrust SDK at all — this prevents the banner
    // from reappearing on every page load.
    if (localStorage.getItem(AppComponent.CONSENT_KEY)) {
      return
    }

    // Prevent duplicate script injection on SPA navigation
    if (document.querySelector('script[data-domain-script]')) {
      return
    }

    // If MSAL is currently processing a redirect (hash contains auth response),
    // skip loading the banner — the page will navigate away shortly.
    if (window.location.hash?.includes('code=')) {
      return
    }

    const startComment = document.createComment(' OneTrust Cookies Consent Notice start for wir.idp.com ')
    document.body.insertBefore(startComment, document.body.firstChild)

    const scriptMain = document.createElement('script')
    scriptMain.src = 'https://cdn-apac.onetrust.com/scripttemplates/otSDKStub.js'
    scriptMain.type = 'text/javascript'
    scriptMain.setAttribute('data-domain-script', environment.oneTrustDomainScript)
    document.body.insertBefore(scriptMain, startComment.nextSibling)

    const scriptWrapper = document.createElement('script')
    scriptWrapper.type = 'text/javascript'
    scriptWrapper.text = 'function OptanonWrapper() { }'
    document.body.insertBefore(scriptWrapper, scriptMain.nextSibling)

    const endComment = document.createComment(' OneTrust Cookies Consent Notice end for wir.idp.com ')
    document.body.insertBefore(endComment, scriptWrapper.nextSibling)

    // Watch for the banner to be dismissed so we can persist the consent
    this.observeBannerDismissal()
  }

  /**
   * Watches the DOM for the OneTrust banner to be hidden/removed.
   * When the user clicks "Yes I agree", OneTrust hides the banner element.
   * We detect that and persist a flag in localStorage so the SDK is never
   * loaded again on subsequent visits.
   */
  private observeBannerDismissal (): void {
    const checkAndPersist = (): void => {
      const banner = document.getElementById('onetrust-banner-sdk')
      if (banner && banner.style.display === 'none') {
        localStorage.setItem(AppComponent.CONSENT_KEY, new Date().toISOString())
        if (this.bannerObserver) {
          this.bannerObserver.disconnect()
          this.bannerObserver = null
        }
      }
    }

    this.bannerObserver = new MutationObserver(() => checkAndPersist())
    this.bannerObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    })
  }

  private injectGtm (): void {
    const gtmId: string = environment.gtmId

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = `
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `
    document.head.appendChild(script)
    const noScript = document.createElement('noscript')
    noScript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `
    document.body.appendChild(noScript)
  }
}
